import { useState, useEffect } from 'react';
import { systemAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Loader2 } from 'lucide-react';

const SystemSettings = () => {
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [settings, setSettings] = useState({
        form16_download_enabled: true
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await systemAPI.getSettings();
            setSettings(response.data);
        } catch (error) {
            toast.error("Failed to fetch system settings");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        setUpdating(true);
        try {
            // Only send the configuration fields, not the whole metadata
            const payload = {
                form16_download_enabled: Boolean(settings.form16_download_enabled)
            };
            const response = await systemAPI.updateSettings(payload);
            setSettings(response.data);
            toast.success("System settings updated successfully");
        } catch (error) {
            console.error(error);
            toast.error("Failed to update settings");
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">System Configuration</h1>
                <p className="text-muted-foreground">Global settings for the HRMS platform</p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Feature Flags</CardTitle>
                    <CardDescription>Enable or disable global features for all companies</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex items-center justify-between space-x-2">
                        <div className="space-y-0.5">
                            <Label htmlFor="form16-toggle">Form 16 Download</Label>
                            <p className="text-xs text-muted-foreground">Allow employees to download their Form 16 PDFs</p>
                        </div>
                        <Switch
                            id="form16-toggle"
                            checked={settings.form16_download_enabled}
                            onCheckedChange={(val) => setSettings(prev => ({ ...prev, form16_download_enabled: val }))}
                        />
                    </div>

                    <div className="pt-4">
                        <Button onClick={handleUpdate} disabled={updating}>
                            {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Save Configuration
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SystemSettings;
